import { DialogHeader } from "../ui/dialog";
import { Subtitle, Title } from "../Text";
import { useTranslation } from "react-i18next";
import { Separator } from "../ui/separator";
import { useMemo, useState } from "react";
import { ArrowLeft, Loader2, RotateCcw } from "lucide-react";
import { ArtmatchResult } from "@/api/artmatch";
import { colorGradient } from "@/lib/utils";
import { Button } from "../ui/button";
import { useNavigate } from "react-router-dom";

export const ArtmatchResults = ({
  baseImage,
  results,
  isPending,
  onReset,
  onNextStep,
  isError,
}: {
  results: ArtmatchResult[];
  isPending: boolean;
  onReset: () => void;
  onNextStep: () => void;
  baseImage: File;
  isError: boolean;
}) => {
  const { t } = useTranslation();
  const [selectedArtwork, setSelectedArtwork] = useState<ArtmatchResult | null>(
    null,
  );
  const navigate = useNavigate();
  const imageSrc = useMemo(() => URL.createObjectURL(baseImage), [baseImage]);

  const handleReset = () => {
    URL.revokeObjectURL(imageSrc);
    onReset();
  };

  const handleClickNoMatch = () => {
    URL.revokeObjectURL(imageSrc);
    onNextStep();
  };

  const handleGoToArtworkPage = () => {
    URL.revokeObjectURL(imageSrc);
    navigate(`/edit/artwork/${selectedArtwork?.artworkId}`);
  };

  return (
    <>
      <DialogHeader className="p-6">
        <Title>{t("artmatch.results.title")}</Title>
      </DialogHeader>
      <div className="flex border-t border-gray-200 items-center justify-between">
        <div className="flex flex-col p-6 gap-3 items-center">
          <img
            src={imageSrc}
            style={{
              width: "100%",
              height: "100%",
              maxWidth: "400px",
              maxHeight: "575px",
              objectFit: "contain",
            }}
          />
          <button
            onClick={handleReset}
            className="flex w-fit gap-1 px-2 py-1 items-center justify-center rounded-lg border border-gray-300"
          >
            <RotateCcw size={20} />
            <p className="font-medium">{t("artmatch.results.newSearch")}</p>
          </button>
        </div>
        {selectedArtwork ? (
          <div className="flex flex-col p-8 gap-6 border-l border-gray-200 bg-gray-50">
            <div
              className="flex items-center justify-start gap-2 hover:cursor-pointer"
              onClick={() => setSelectedArtwork(null)}
            >
              <ArrowLeft size={18} color="#008BD4" />
              <p className="font-semibold text-brand-600 hover:underline">
                {t("artmatch.results.backToResults")}
              </p>
            </div>
            <div className="flex flex-col p-6 gap-6">
              <ArtmatchResultsItem
                artwork={selectedArtwork}
                imageMaxWidth="500px"
                imageMaxHeight="600px"
              />
              <div className="flex flex-col gap-6 items-center">
                <p className="font-medium text-center text-xl">
                  {t("artmatch.results.seeArtworkInCR")}
                </p>
                <Button className="w-fit" onClick={handleGoToArtworkPage}>
                  {t("artmatch.results.goToArtworkPage")}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col border-l border-gray-200">
            <div className="flex flex-col p-6 gap-3 items-center">
              <Title className="text-center">
                {t("artmatch.results.matchYes")}
              </Title>
              <div className="flex w-[45%] items-center gap-3 justify-center">
                <Separator />
                <p>{t("OR")}</p>
                <Separator />
              </div>
              <div className="flex flex-col items-center">
                <Title className="text-center">
                  {t("artmatch.results.matchNo")}
                </Title>
                <Subtitle
                  className="text-center underline underline-offset-2 hover:cursor-pointer hover:text-blue-500"
                  onClick={handleClickNoMatch}
                >
                  {t("artmatch.results.matchNo.clickHereBackOffice")}
                </Subtitle>
              </div>
            </div>
            {isPending ? (
              <div className="flex flex-col min-h-[400px] p-6 gap-6 bg-gray-50 border-t border-gray-200 items-center justify-center">
                <Loader2 className="animate-spin" size={40} color="gray" />
              </div>
            ) : (
              <div className="flex flex-col min-h-[400px] p-6 gap-6 bg-gray-50 border-t border-gray-200">
                <p className="px-2 py-1 w-fit bg-white rounded-md border border-gray-300 font-medium">
                  {results.length} {t("artmatch.results.potentialResults")}
                </p>
                {results.length > 0 ? (
                  <div className="grid grid-cols-3 gap-6 items-start max-h-[600px] overflow-y-auto">
                    {results.map((result) => (
                      <ArtmatchResultsItem
                        key={result.artworkId}
                        artwork={result}
                        showScore={true}
                        onClick={(artwork) => setSelectedArtwork(artwork)}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="flex flex-col min-h-[400px] p-6 items-center justify-center text-sm text-gray-500">
                    {isError
                      ? t("artmatch.results.error")
                      : t("artmatch.results.noResults")}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

const ArtmatchResultsItem = ({
  artwork,
  showScore = false,
  onClick,
  imageMaxWidth = "300px",
  imageMaxHeight = "400px",
}: {
  artwork: ArtmatchResult;
  showScore?: boolean;
  onClick?: (artwork: ArtmatchResult) => void;
  imageMaxWidth?: string;
  imageMaxHeight?: string;
}) => {
  return (
    <div
      className="relative flex flex-col gap-2"
      onClick={() => onClick?.(artwork)}
    >
      {showScore && (
        <div
          className="absolute top-0 left-0 px-2 py-1 rounded-br-xl"
          style={{
            zIndex: 100,
            backgroundColor: colorGradient(
              artwork.similarity_score,
              "#FF0D0D",
              "#FAB733",
              "#69B34C",
            ),
          }}
        >
          <p className="text-xs font-semibold">
            {Math.round(artwork.similarity_score * 100)}
            {"%"}
          </p>
        </div>
      )}
      <img
        src={artwork.url}
        style={{
          width: "100%",
          height: "100%",
          maxWidth: imageMaxWidth,
          maxHeight: imageMaxHeight,
          objectFit: "contain",
        }}
      />
      <div className="flex flex-col gap-1 items-center">
        <p className="text-base">
          {artwork.author_firstname ?? ""} {artwork.author_name ?? ""}
        </p>
        <p className="text-sm font-semibold">{artwork.title}</p>
        <p className="text-xs">{artwork.year}</p>
      </div>
    </div>
  );
};
